import { CenteredDialog } from '../ui/centered-dialog';
// apps/web/src/components/qualidade/destinacao-dialog.tsx — Fase 02, Plano 09 (QUAL-02, QUAL-03, D-14).
import { useState, type ReactElement } from 'react';
import { apiMutate } from '../../lib/csrf-fetch';
import { rotuloDestinacao } from '../../lib/rotulos';
import { CelulaSelect } from '../estoque/celula-select';

export type DestinacaoTipo = 'SUCATA' | 'RETRABALHO' | 'REFAZER';

interface DestinacaoDialogProps {
  readonly ncId: string;
  readonly motivo: string;
  readonly quantidade: number;
  /** UUID da célula ativa da etapa de origem, fornecido pela lista de NCs. */
  readonly celulaOrigemId: string | null;
  readonly celulaOrigemCodigo: string | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onDestinado: () => void;
}

export function DestinacaoDialog({
  ncId,
  motivo,
  quantidade,
  celulaOrigemId,
  celulaOrigemCodigo,
  open,
  onClose,
  onDestinado,
}: DestinacaoDialogProps): ReactElement | null {
  const [destinacao, setDestinacao] = useState<DestinacaoTipo | ''>('');
  const [etapaReentrada, setEtapaReentrada] = useState<string>('1');
  const [celulaId, setCelulaId] = useState<string>(celulaOrigemId ?? '');
  const [justification, setJustification] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const requiresEtapa = destinacao === 'RETRABALHO' || destinacao === 'REFAZER';
  const isFormValid =
    destinacao !== '' &&
    (!requiresEtapa || (Boolean(etapaReentrada) && Boolean(celulaId))) &&
    Boolean(justification.trim());

  const handleConfirmar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !destinacao) return;

    setLoading(true);
    setError(null);
    try {
      const res = await apiMutate(`/api/v1/qualidade/nc/${encodeURIComponent(ncId)}/destinar`, {
        method: 'POST',
        body: {
          destinacao,
          etapaReentrada: requiresEtapa ? parseInt(etapaReentrada, 10) : undefined,
          celulaId: requiresEtapa ? celulaId : undefined,
          justification: justification.trim(),
        },
      });

      if (!res.ok) {
        const errJson = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(errJson.message || 'Falha ao destinar Não Conformidade.');
      }

      onDestinado();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao destinar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CenteredDialog title="Destinar não conformidade" onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full p-6 space-y-5">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Destinar não conformidade</h3>
            <p className="text-xs text-slate-500 mt-0.5">Definição formal pela equipe de Qualidade</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 font-bold text-base"
          >
            ✕
          </button>
        </div>

        <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-500 font-semibold">Quantidade Afetada:</span>
            <span className="font-mono font-bold text-slate-800">{quantidade} peças</span>
          </div>
          <div className="text-slate-700">
            <span className="text-slate-500 font-semibold">Motivo: </span>
            {motivo}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleConfirmar(e)} className="space-y-4">
          {/* Opções de Destinação (Sem seleção padrão) */}
          <div className="space-y-2">
            <label className="block text-xs font-bold uppercase text-slate-700">
              Selecione a Destinação *
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['RETRABALHO', 'REFAZER', 'SUCATA'] as const).map((opt) => (
                <label
                  key={opt}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer text-center transition ${
                    destinacao === opt
                      ? opt === 'SUCATA'
                        ? 'border-red-600 bg-red-50 text-red-900 font-bold'
                        : 'border-blue-600 bg-blue-50 text-blue-900 font-bold'
                      : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 font-medium'
                  }`}
                >
                  <input
                    type="radio"
                    name="destinacao"
                    value={opt}
                    checked={destinacao === opt}
                    onChange={() => setDestinacao(opt)}
                    className="sr-only"
                  />
                  <span className="text-xs">{rotuloDestinacao(opt)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Campos condicionais para Retrabalho / Refazer */}
          {requiresEtapa && (
            <div className="p-4 bg-blue-50/60 border border-blue-200 rounded-xl space-y-3 animate-fade-in">
              <h4 className="text-xs font-bold text-blue-900 uppercase">
                Parâmetros da OS corretiva
              </h4>
              <p className="text-[11px] text-blue-700">
                A OS original permanece intacta; uma nova OS corretiva será criada na etapa indicada.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Etapa de Reentrada *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={etapaReentrada}
                    onChange={(e) => setEtapaReentrada(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Célula / Posto *
                  </label>
                  <CelulaSelect
                    id="destinacao-celula"
                    value={celulaId}
                    onChange={setCelulaId}
                    codigoSelecionado={celulaOrigemCodigo}
                    invalido={celulaId === ''}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Confirmação Destrutiva para Sucata */}
          {destinacao === 'SUCATA' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-800 space-y-1">
              <span className="font-bold">Atenção: Ação Destrutiva</span>
              <p>
                Destinar este lote como sucata registrará uma baixa por sinistro no estoque e não gerará ordens de
                serviço corretivas.
              </p>
            </div>
          )}

          {/* Justificativa Obrigatória */}
          <div>
            <label className="block text-xs font-bold uppercase text-slate-700 mb-1">
              Justificativa Técnica *
            </label>
            <textarea
              required
              rows={2}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Descreva a fundamentação técnica da decisão..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-100"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !isFormValid}
              className={`px-5 py-2 text-white text-xs font-bold rounded-lg shadow transition disabled:opacity-40 ${
                destinacao === 'SUCATA' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading
                ? 'Destinando...'
                : destinacao === 'SUCATA'
                  ? `Destinar ${quantidade} peças como sucata`
                  : 'Destinar não conformidade'}
            </button>
          </div>
        </form>
      </div>
    </CenteredDialog>
  );
}
